import React from 'react'
import ReactMarkdown from 'react-markdown'

interface MarkdownRendererProps {
  content: string
  style?: React.CSSProperties
}

export function MarkdownRenderer({ content, style }: MarkdownRendererProps) {
  return (
    <div style={style}>
      <ReactMarkdown
        components={{
          // Estiliza links para abrir em nova aba
          a: ({ children, href, ...props }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#0072BB',
                textDecoration: 'underline',
                fontWeight: 500,
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#005fa3'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#0072BB'
              }}
            >
              {children}
            </a>
          ),
          // Estiliza parágrafos
          p: ({ children, ...props }) => (
            <p
              style={{
                fontSize: '1rem',
                lineHeight: 1.6,
                color: '#333',
                margin: '0 0 1rem 0',
                whiteSpace: 'pre-line',
                textAlign: 'left',
                fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
              }}
            >
              {children}
            </p>
          ),
          // Estiliza listas
          ul: ({ children, ...props }) => (
            <ul
              style={{
                margin: '0 0 1rem 0',
                paddingLeft: '1.5rem'
              }}
            >
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol
              style={{
                margin: '0 0 1rem 0',
                paddingLeft: '1.5rem'
              }}
            >
              {children}
            </ol>
          ),
          // Estiliza itens de lista
          li: ({ children, ...props }) => (
            <li
              style={{
                marginBottom: '0.5rem',
                lineHeight: 1.6
              }}
            >
              {children}
            </li>
          ),
          // Estiliza títulos
          h1: ({ children, ...props }) => (
            <h1
              style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#0072BB',
                margin: '1.5rem 0 1rem 0',
                lineHeight: 1.3
              }}
            >
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2
              style={{
                fontSize: '1.3rem',
                fontWeight: '600',
                color: '#0072BB',
                margin: '1.3rem 0 0.8rem 0',
                lineHeight: 1.3
              }}
            >
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3
              style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#333',
                margin: '1.1rem 0 0.6rem 0',
                lineHeight: 1.3
              }}
            >
              {children}
            </h3>
          ),
          // Estiliza código inline
          code: ({ children, className, ...props }) => {
            const isInline = !className?.includes('language-')
            if (isInline) {
              return (
                <code
                  style={{
                    backgroundColor: '#f5f5f5',
                    padding: '0.2rem 0.4rem',
                    borderRadius: '3px',
                    fontSize: '0.9rem',
                    fontFamily: 'monospace',
                    color: '#e83e8c'
                  }}
                >
                  {children}
                </code>
              )
            }
            return (
              <code
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: '0.5rem',
                  borderRadius: '5px',
                  fontSize: '0.9rem',
                  fontFamily: 'monospace',
                  display: 'block',
                  overflow: 'auto',
                  color: '#333'
                }}
              >
                {children}
              </code>
            )
          },
          // Estiliza blocos de código
          pre: ({ children, ...props }) => (
            <pre
              style={{
                backgroundColor: '#f8f9fa',
                padding: '1rem',
                borderRadius: '5px',
                overflow: 'auto',
                margin: '1rem 0',
                border: '1px solid #e9ecef'
              }}
            >
              {children}
            </pre>
          ),
          // Estiliza blockquotes
          blockquote: ({ children, ...props }) => (
            <blockquote
              style={{
                borderLeft: '4px solid #0072BB',
                paddingLeft: '1rem',
                margin: '1rem 0',
                fontStyle: 'italic',
                color: '#666'
              }}
            >
              {children}
            </blockquote>
          ),
          // Estiliza texto forte
          strong: ({ children, ...props }) => (
            <strong
              style={{
                fontWeight: '700',
                color: '#333'
              }}
            >
              {children}
            </strong>
          ),
          // Estiliza texto em itálico
          em: ({ children, ...props }) => (
            <em
              style={{
                fontStyle: 'italic',
                color: '#555'
              }}
            >
              {children}
            </em>
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
} 